import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AtSign, MessageSquare, Search, Flag, Clock, ExternalLink, ChevronRight, UserRound } from 'lucide-react';
import clsx from 'clsx';
import { useMessengerView } from '@/api/views';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useTourStore } from '@/state/tourStore';
import { useUIStore } from '@/state/uiStore';
import { currentActorId } from '@/domain/actor';
import { useActiveCall } from '@/hooks/useActiveCall';
import { buildConversationsFeed, type ConversationFeedItem } from '@/utils/conversationsFeed';
import { isMessengerThread, messengerWindow, needsReply, windowNowIso, type MessengerWindow } from '@/domain/messengerWindow';
import ConversationThread from '@/components/ConversationThread';
import CustomerComposer from '@/components/CustomerComposer';
import InstagramLink from '@/components/InstagramLink';
import CustomerBriefControls from '@/components/CustomerBriefControls';
import SlotDepositPane from '@/components/SlotDepositPane';
import CustomerActionModals, { type CustomerAction } from '@/components/CustomerActionModals';
import ReconnectingNotice from '@/components/shell/ReconnectingNotice';
import { useActiveStudio } from '@/state/useActiveStudio';
import { isAllStudios, ALL_STUDIOS_SCOPE, activeStudioIds } from '@/domain/studioScope';
import { customerName, relativeFromIso, simTimeShort } from '@/utils/format';
import type { CustomerRecord, StudioId } from '@/domain/types';

// The Messenger owns IG + FB DMs only (System Diagram 03). Anything with a phone
// number (email, inbound call) is Agent intake and lives on My Queue.

type Folder = 'all' | MessengerWindow['phase'] | 'sent-to-agent';

const FOLDERS: Array<{ id: Folder; label: string; warn?: boolean }> = [
  { id: 'all', label: 'All' },
  { id: 'needs-reply', label: 'Needs reply' },
  { id: 'closing-soon', label: 'Closing soon', warn: true },
  { id: 'awaiting', label: 'Awaiting client' },
  { id: 'sent-to-agent', label: 'Sent to agent' },
];

const igHandle = (c: CustomerRecord): string | undefined =>
  (c as { instagramHandle?: string }).instagramHandle;

function initials(c: CustomerRecord): string {
  const a = c.firstName?.[0] ?? '';
  const b = c.lastName?.[0] ?? '';
  return (a + b).toUpperCase() || (igHandle(c)?.[1] ?? '?').toUpperCase();
}

function phaseChip({ phase, hoursLeft, agentHoursLeft }: MessengerWindow): { label: string; cls: string; agent?: boolean } {
  // A real HookChat thread 24h–7d out: the 24h window is shut, but an agent can
  // still send (tv-isee), so it is not "Window closed".
  if (agentHoursLeft !== undefined) {
    return {
      label: `Agent replies only · ${Math.max(1, Math.floor(agentHoursLeft / 24))}d left`,
      cls: 'bg-orange/12 text-orange border-orange/40',
      agent: true,
    };
  }
  const h = Math.max(0, Math.round(hoursLeft));
  switch (phase) {
    case 'needs-reply':
      return { label: `Your turn · ${h}h`, cls: 'bg-azure/12 text-azure border-azure/40' };
    case 'closing-soon':
      return { label: `Closing · ${h}h`, cls: 'bg-orange/12 text-orange border-orange/40' };
    case 'awaiting':
      return { label: `Awaiting · ${h}h`, cls: 'bg-mint/10 text-mint border-mint/35' };
    case 'elapsed':
      return { label: 'Window closed', cls: 'bg-ink-soft text-cream-faint border-gold-dim/30' };
  }
}

export default function MessengerInbox() {
  const navigate = useNavigate();
  // Live reads (tv-ods.3): customers/sequenceState/studios come from the
  // network-wide messenger view (/api/views/messenger + /ws), not brainStore.
  // incomingCalls/clockIso/activeCall/assignedStudios/activeStudio stay on
  // their ui/sim/session stores below. (tv-nhs: activityFeed dropped from this
  // view — ledger writes stay dormant, re-addable later.)
  const messenger = useMessengerView();
  const customers = messenger.data?.customers ?? {};
  const sequenceState = messenger.data?.sequenceState ?? {};
  const studios = messenger.data?.studios ?? {};
  const incomingCalls = useUIStore((s) => s.incomingCalls);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const activeCall = useActiveCall();
  // Studio scope follows the top-bar switcher live (single source of truth).
  const { activeStudio, assignedStudios } = useActiveStudio();
  const setActiveStudio = useSessionStore((s) => s.setActiveStudio);

  const [folder, setFolder] = useState<Folder>('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState<'brief' | 'availability'>('brief');
  const [openModal, setOpenModal] = useState<CustomerAction>(null);

  // IG/FB threads only, each decorated with its 24h-window phase.
  const items = useMemo(() => {
    const feed = buildConversationsFeed({
      customers,
      activeCall: activeCall
        ? { customerId: activeCall.customerId, status: activeCall.status }
        : null,
      // A Sweeper's routed callback (B7) rings that Sweeper only, not this inbox.
      incomingCalls: incomingCalls.filter((c) => !c.routedToPid || c.routedToPid === currentActorId()),
      sequenceState,
      nowIso: clockIso,
    }).filter((i) => isMessengerThread(i.customer));
    return feed.map((i) => ({ item: i, win: messengerWindow(i.customer, clockIso) }));
  }, [customers, activeCall, incomingCalls, sequenceState, clockIso]);

  // The studio + search scope both the rendered list and every folder badge —
  // but NOT the selected folder, so each pill keeps its own count. Shared so the
  // badges can never disagree with the rows the same filters would render.
  const inScope = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (item: ConversationFeedItem): boolean => {
      // Live studio scope from the switcher; 'all'/unset passes everything.
      if (!isAllStudios(activeStudio) && item.studioId !== activeStudio) return false;
      if (q) {
        const c = item.customer;
        const hay = `${c.firstName} ${c.lastName} ${igHandle(c) ?? ''} ${item.preview}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    };
  }, [activeStudio, query]);

  const filtered = useMemo(() => {
    return items.filter(({ item, win }) => {
      // A genuinely handed-off thread (sentToAgent) leaves the normal folders and
      // lives only under "Sent to agent"; every other folder excludes it. A
      // merely-contacted thread (inQueue but not sentToAgent) stays put.
      if (folder === 'sent-to-agent') {
        if (!item.sentToAgent) return false;
      } else {
        if (item.sentToAgent) return false;
        if (folder === 'needs-reply') {
          // 7q73: every client-last thread, closing soon or past 24h included.
          if (!needsReply(win)) return false;
        } else if (folder !== 'all' && win.phase !== folder) return false;
      }
      return inScope(item);
    });
  }, [items, folder, inScope]);

  const counts = useMemo(() => {
    const c: Record<Folder, number> = {
      all: 0,
      'needs-reply': 0,
      'closing-soon': 0,
      awaiting: 0,
      elapsed: 0,
      'sent-to-agent': 0,
    };
    for (const { item, win } of items) {
      // Each folder's badge equals what that folder would render under the active
      // studio + search: out-of-scope threads count nowhere. Within scope,
      // genuinely handed-off threads count only under "Sent to agent"; everything
      // else (incl. merely-contacted threads) feeds All + its phase bucket.
      if (!inScope(item)) continue;
      if (item.sentToAgent) {
        c['sent-to-agent'] += 1;
        continue;
      }
      c.all += 1;
      // 7q73: Needs reply counts every client-last thread; Closing soon and
      // Awaiting stay window phases (a closing-soon client-last thread counts in
      // both Needs reply and Closing soon).
      if (needsReply(win)) c['needs-reply'] += 1;
      if (win.phase !== 'needs-reply') c[win.phase] += 1;
    }
    return c;
  }, [items, inScope]);

  // Keep a valid selection as the list changes.
  useEffect(() => {
    if (filtered.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!selectedId || !filtered.some((f) => f.item.customerId === selectedId)) {
      setSelectedId(filtered[0]!.item.customerId);
    }
  }, [filtered, selectedId]);

  const selected = selectedId ? customers[selectedId] : undefined;
  const selectedWin = selected ? messengerWindow(selected, clockIso) : undefined;
  const selectedChip = selectedWin ? phaseChip(selectedWin) : undefined;

  const startTour = useTourStore((s) => s.start);
  const tourEnabled = useTourStore((s) => s.enabled);
  // Re-run on `tourEnabled` too: Demo off preserves the active tour (pause), so
  // when it flips back on we must re-baseline against whichever thread is now
  // open. start()'s guard no-ops if it's the same thread (true resume-in-place)
  // and re-baselines if a different thread was opened while paused — preventing
  // a resumed tour from ringing the wrong customer's UI.
  useEffect(() => {
    if (tourEnabled && selected) startTour('messenger', selected.id);
  }, [selected?.id, tourEnabled, startTour]);

  return (
    <section className="flex h-full min-h-0 overflow-hidden" data-testid="messenger-inbox">
      {/* LEFT · conversation list */}
      <aside className="w-[340px] shrink-0 flex flex-col min-h-0 border-r border-gold-dim/15 bg-ink-deep">
        <header className="px-5 pt-4 pb-3">
          <div className="flex items-center justify-between gap-2">
            <h1 className="heading-display text-xl text-cream">Inbox</h1>
          </div>
          <p className="font-serif text-cream-soft mt-1 text-xs">IG &amp; FB DMs · newest first</p>
          <label className="mt-3 flex items-center gap-2 rounded-md border border-gold-dim/25 bg-ink-black px-3 py-2">
            <Search size={12} className="text-cream-faint" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, @handle, message…"
              className="flex-1 bg-transparent text-sm text-cream focus:outline-none"
              data-testid="messenger-search"
            />
          </label>
        </header>

        <div className="px-5 pb-3 flex gap-1.5 flex-wrap">
          {FOLDERS.map((f) => {
            const on = folder === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFolder(f.id)}
                data-testid={`messenger-folder-${f.id}`}
                className={clsx(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-inter uppercase tracking-widest transition',
                  on
                    ? 'border-gold-bright bg-gold/15 text-gold-bright'
                    : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
                )}
              >
                {f.label}
                <span
                  className={clsx(
                    'px-1.5 rounded-full text-[10px] font-mono',
                    f.warn && counts[f.id] > 0
                      ? 'bg-orange/15 text-orange'
                      : on
                        ? 'bg-gold-bright/25 text-gold-bright'
                        : 'bg-ink-soft text-cream-soft',
                  )}
                >
                  {counts[f.id]}
                </span>
              </button>
            );
          })}
        </div>

        {assignedStudios.length > 1 ? (
          <div className="px-5 pb-3">
            <select
              value={activeStudio ?? ALL_STUDIOS_SCOPE}
              onChange={(e) => setActiveStudio(e.target.value as StudioId | 'all')}
              className="w-full bg-ink-black border border-gold-dim/40 rounded px-2 py-1.5 text-xs text-cream"
              data-testid="messenger-studio-filter"
            >
              <option value={ALL_STUDIOS_SCOPE}>All studios</option>
              {/* branding-3: archived studios drop out (the current pick stays). */}
              {activeStudioIds(studios, assignedStudios)
                .concat(activeStudio && !isAllStudios(activeStudio) && studios[activeStudio]?.archived ? [activeStudio as StudioId] : [])
                .map((id) => (
                <option key={id} value={id}>
                  {studios[id]?.name ?? id}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <ol className="flex-1 min-h-0 overflow-y-auto border-t border-gold-dim/15" data-testid="messenger-list">
          {filtered.length === 0 ? (
            <li className="px-5 py-10 text-center font-serif text-cream-soft text-sm">
              {messenger.isError && Object.keys(customers).length === 0
                ? <ReconnectingNotice />
                : 'No conversations here.'}
            </li>
          ) : null}
          {filtered.map(({ item, win }) => (
            <ThreadRow
              key={item.customerId}
              item={item}
              win={win}
              selected={item.customerId === selectedId}
              onSelect={() => setSelectedId(item.customerId)}
            />
          ))}
        </ol>
      </aside>

      {/* CENTRE · thread */}
      <section className="flex-1 flex flex-col min-w-0 min-h-0" data-testid="messenger-thread-pane">
        {selected ? (
          <>
            <header className="px-6 py-3 border-b border-gold-dim/15 bg-ink-deep flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-azure/20 text-azure grid place-items-center font-inter font-bold text-sm shrink-0">
                {initials(selected)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-inter text-sm text-cream truncate">{customerName(selected)}</p>
                <p className="font-mono text-[11px] text-cream-faint mt-0.5 truncate">
                  {igHandle(selected) ? <InstagramLink handle={igHandle(selected)} /> : null}
                  {igHandle(selected) ? ' · ' : ''}
                  {selected.primaryStudioId.replace('-', ' ')}
                </p>
              </div>
              {selectedChip ? (
                <span
                  className={clsx(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[10px] font-inter uppercase tracking-widest',
                    selectedChip.cls,
                  )}
                  data-testid="messenger-window-chip"
                >
                  {selectedChip.agent ? <UserRound size={10} /> : <Clock size={10} />}
                  {selectedChip.label}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => navigate(`/customer/${selected.id}`)}
                className="btn-ghost text-[10px] shrink-0"
                data-testid="messenger-open-full-record"
              >
                <ExternalLink size={11} /> Full record
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-8 py-6 min-h-0">
              <ConversationThread customer={selected} />
            </div>
            <CustomerComposer customer={selected} host="messenger" onOpenModal={setOpenModal} />
          </>
        ) : (
          <div className="flex-1 grid place-items-center px-6">
            <p className="font-serif text-cream-soft text-center max-w-xs">
              Pick a thread to reply, book, or hand to an agent.
            </p>
          </div>
        )}
      </section>

      {/* RIGHT · the focused record's Brief / Availability */}
      <aside className="w-[380px] shrink-0 flex flex-col min-h-0 border-l border-gold-dim/15 bg-ink-deep">
        {selected ? (
          <>
            <div className="flex border-b border-gold-dim/15">
              {(['brief', 'availability'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setRightTab(t)}
                  data-testid={`messenger-tab-${t}`}
                  className={clsx(
                    'flex-1 py-3 text-[11px] font-inter uppercase tracking-widest transition border-b-2',
                    rightTab === t
                      ? 'text-gold-bright border-gold-bright bg-gold/[0.06]'
                      : 'text-cream-faint border-transparent hover:text-cream-soft',
                  )}
                >
                  {t === 'brief' ? 'Design Brief' : 'Artist Availability'}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5 min-h-0 space-y-6" data-testid="messenger-right-pane">
              {rightTab === 'brief' ? (
                <>
                  <CustomerBriefControls customer={selected} idPrefix="messenger-brief" />
                  <button
                    type="button"
                    onClick={() => setRightTab('availability')}
                    className="btn-ghost w-full justify-center text-xs"
                    data-testid="messenger-brief-check-availability"
                  >
                    Check availability <ChevronRight size={14} />
                  </button>
                </>
              ) : (
                <SlotDepositPane customer={selected} />
              )}
            </div>
          </>
        ) : null}
      </aside>

      {selected ? (
        <CustomerActionModals customer={selected} open={openModal} onClose={() => setOpenModal(null)} />
      ) : null}
    </section>
  );
}

function ThreadRow({
  item,
  win,
  selected,
  onSelect,
}: {
  item: ConversationFeedItem;
  win: MessengerWindow;
  selected: boolean;
  onSelect: () => void;
}) {
  const c = item.customer;
  const ChannelIcon = item.latestChannel === 'ig-dm' ? AtSign : MessageSquare;
  const chip = phaseChip(win);
  return (
    <li
      data-testid={`messenger-row-${item.customerId}`}
      className={clsx(
        'border-b border-gold-dim/10 transition',
        selected ? 'bg-ink-soft/60' : 'hover:bg-ink-soft/40',
        item.sentToAgent ? 'opacity-50' : '',
      )}
    >
      <button type="button" onClick={onSelect} className="w-full text-left px-5 py-3">
        <div className="flex items-baseline gap-2">
          <span className="font-inter text-sm text-cream truncate flex-1">{customerName(c)}</span>
          <span className="font-mono text-[10px] text-cream-faint shrink-0">
            {simTimeShort(item.lastActivityIso)}
          </span>
        </div>
        <p className="font-mono text-[10px] text-azure mt-0.5 truncate">
          <ChannelIcon size={9} className="inline mr-1" />
          {/* Plain text, not InstagramLink: this whole row is inside a <button
              onClick={onSelect}> above, and an <a> nested in a <button> is
              invalid HTML / would fight the row-select click. The thread
              header and customer record header already provide the link. */}
          {igHandle(c) ?? item.latestChannel.replace('-', ' ')} · {c.primaryStudioId.replace('-', ' ')}
        </p>
        <p className="font-serif text-sm text-cream-soft mt-1 truncate">{item.preview}</p>
        <div className="mt-2 flex items-center gap-2">
          {item.sentToAgent ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-mono border bg-azure/12 text-azure border-azure/40">
              <Flag size={9} /> In queue
            </span>
          ) : (
            <span
              className={clsx(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-mono border',
                chip.cls,
              )}
            >
              {chip.agent ? <UserRound size={9} /> : null}
              {chip.label}
            </span>
          )}
          <span className="font-mono text-[9.5px] text-cream-faint">
            {relativeFromIso(item.lastActivityIso, windowNowIso(c, useSimulationStore.getState().clockIso))}
          </span>
        </div>
      </button>
    </li>
  );
}
